"use client";

import { useState, useEffect, useRef } from "react";
import { useTranslations } from "next-intl";
import type {
  AnyProduct,
  ConfigurableProduct,
  GroupedProduct,
  BundleProduct,
  Price as PriceType,
} from "@cobrastyle/shared-types";
import { cn } from "@cobrastyle/ui";
import { Button } from '@/components/ui/button';
import { useCart, useAnalytics } from "@/hooks";
import { useCartFeedback } from "@/hooks/use-cart-feedback";
import { useFreeGiftClaim } from "@/hooks/use-free-gift-claim";
import type { FreeGift } from "@/lib/free-gift/types";
import { FreeGiftOffer } from "./free-gift-offer";
import { useCartDrawer } from "@/components/cart/cart-drawer-provider";
import { ImageGallery } from "./image-gallery";
import { ProductAccordion, type AccordionSection } from "./product-accordion";
import { SpecGrid, SpecTable } from "./spec-grid";
import { LicenseNotice } from "./license-notice";
import { LicenseProofNotice } from "./license-proof-notice";
import { requiresLicenseProof } from "@/lib/license-proof";
import { DeliveryInfo } from "./delivery-info";
import { ReadMore } from "./read-more";
import { ProductBadge, type BadgeTone } from "./product-badge";
import { QuantitySelector } from "./quantity-selector";
import { OptionButton } from "./option-button";
import { GroupedProductItems } from "./grouped-product-items";
import { StickyAddToCart } from "./sticky-add-to-cart";
import { WishlistButton } from "./wishlist-button";
import { CompareCheckbox } from "@/components/compare";
import { VolumePricingTable } from "./volume-pricing-table";
import { BundleOptions } from "./bundle-options";
import { PaketIncludes } from "./paket-includes";
import { PaketIncludesList } from "./paket-includes-list";
import type { PaketItem } from "@/lib/paket";
import { formatPrice } from "@/lib/format-price";
import { referencePriceFor } from "@/lib/reference-price";
import { galleryImages } from "@/lib/variant-images";
import type { ProductExtras } from "@/lib/product-extras";
import {
  clampQuantity,
  DEFAULT_QUANTITY_RULES,
  quantityRules,
} from "@/lib/quantity-rules";
import {
  resolveSection,
  deliveryTitleKey,
} from "@/lib/product-sections";
import {
  sortOptionValues,
  swatchSize,
  OPTIONS_AS_SELECT_FROM,
} from "@/lib/configurable-options";
import {
  attributeValue,
  isPickupOnly,
  productKind,
  requiresLicense,
  selectSpecs,
  technicalSpecs,
} from "@/lib/product-specs";
import {
  computeBundleTotal,
  validateBundleSelections,
  buildSelectedOptionUids,
  defaultBundleState,
  type BundleSelectionState,
} from "@/lib/bundle";

interface ProductDetailsProps {
  product: AnyProduct;
  /** Server-rendered PageBuilder/plain-HTML node for the long description (shown in the accordion). */
  descriptionContent?: React.ReactNode;
  /** Server-rendered PageBuilder/plain-HTML node for the short description. */
  shortDescriptionContent?: React.ReactNode;
  /** The gift this product grants, when it grants one. */
  freeGift?: FreeGift | null;
  /**
   * Brand and badge flags, fetched server-side. Optional: without it the page
   * still renders, just without the brand eyebrow and the NYINKOMMET badge —
   * `new_from_date` is not on the product query, so recency cannot be derived
   * from `product` alone.
   */
  extras?: ProductExtras;
  /**
   * The products this vapenpaket contains, resolved server-side from
   * `th_paket_innehall`.
   *
   * Absent for everything that is not a paket — which is the whole catalogue bar
   * a handful — and that absence is what keeps every other product page byte-for
   * -byte unchanged.
   *
   * `referencePrice` is resolved server-side and arrives already validated
   * against the package price. It cannot be read here: `th_rekpris` is not
   * visible-on-front, so it is absent from `product.attributes` entirely — see
   * lib/paket-attributes.ts. A paket shows it whatever its `th_pricelogic`, and
   * with a savings line; every other product shows the same attribute only under
   * the price codes that quote against it, without one — see
   * lib/reference-price.ts.
   */
  paket?: { items: PaketItem[]; referencePrice: number | null };
}

export function ProductDetails({
  product,
  descriptionContent,
  shortDescriptionContent,
  freeGift = null,
  extras,
  paket,
}: ProductDetailsProps) {
  const { addToCart, isLoading } = useCart();
  const { trackViewItem, trackAddToCart } = useAnalytics();
  const { notify } = useCartFeedback();
  const { claim: claimFreeGift } = useFreeGiftClaim();
  const { open: openCartDrawer } = useCartDrawer();
  const t = useTranslations("product");
  const tToast = useTranslations("toast");
  // Minimum order and pack size, from Jeeves (lib/quantity-rules.ts). Only the
  // main box of a simple or configurable product is governed: a grouped
  // product's rows and a bundle's options keep their own counts. A
  // configurable reads its PARENT's attributes — the shared variant query
  // carries only the option attributes, and no configurable in the catalogue
  // has either attribute today. The cart reads the purchased variant.
  const governsQuantity = product.type !== "grouped" && product.type !== "bundle";
  const minOrder = governsQuantity ? extras?.minOrderQuantity : undefined;
  const packSize = governsQuantity ? extras?.packSize : undefined;
  const rules = governsQuantity
    ? quantityRules({ minOrder, packSize })
    : DEFAULT_QUANTITY_RULES;
  // Starts AT the minimum, like the old shop: a box reading 1 on a product
  // sold by the thousand would be refused on the first press.
  const [quantity, setQuantity] = useState(rules.min);
  // Extras can arrive after the first render of a reused component; keep the
  // quantity valid when the rules change under it.
  useEffect(() => {
    setQuantity((current) => clampQuantity(current, rules));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rules.min, rules.step, rules.max]);
  // Ticked from the start (p.9 of the feedback), but only when the gift can
  // actually be had: pre-ticking an out-of-stock gift draws the panel in its
  // claimed state for something nobody can receive. Lazy initialiser rather
  // than an effect — the first paint is already correct.
  const [claimGift, setClaimGift] = useState(() => freeGift?.inStock === true);
  // How many times a buy button has been pressed while an option group was
  // unanswered. A count rather than a flag: it keys the message below, so React
  // remounts it on every press and the CSS animation replays — a class added to
  // an already-mounted node does not restart one. Zero on render, because the
  // prompt is a response to an attempt and an untouched page says nothing.
  const [optionPromptCount, setOptionPromptCount] = useState(0);
  const [selectedOptions, setSelectedOptions] = useState<
    Record<string, string>
  >({});

  // State for grouped product item quantities
  const [groupedQuantities, setGroupedQuantities] = useState<
    Record<string, number>
  >({});

  // State for bundle product option selections
  const [bundleSelections, setBundleSelections] = useState<BundleSelectionState>({});

  // Refs to prevent duplicate event tracking
  const hasTrackedView = useRef(false);
  const trackedProductId = useRef<string | null>(null);
  const isAddingToCart = useRef(false);

  const isConfigurable = product.type === "configurable";
  const isGrouped = product.type === "grouped";
  const isBundle = product.type === "bundle";
  const configurableProduct = isConfigurable
    ? (product as ConfigurableProduct)
    : null;
  const groupedProduct = isGrouped ? (product as GroupedProduct) : null;
  const bundleProduct = isBundle ? (product as BundleProduct) : null;

  // Initialize grouped quantities when product changes
  useEffect(() => {
    if (isGrouped && groupedProduct) {
      const initialQuantities: Record<string, number> = {};
      groupedProduct.items.forEach((item) => {
        initialQuantities[item.product.sku] = item.quantity || 0;
      });
      setGroupedQuantities(initialQuantities);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.id, isGrouped]);

  // Initialize bundle option defaults when product changes
  useEffect(() => {
    if (isBundle && bundleProduct) {
      setBundleSelections(defaultBundleState(bundleProduct.bundleOptions));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.id, isBundle]);

  // Auto-select any option group that has only one value. Lets the customer
  // pick once on a multi-attribute product where only colour varies, instead
  // of asking them to "select" an inevitable size of 9ml.
  useEffect(() => {
    if (!configurableProduct) return;
    const auto: Record<string, string> = {};
    for (const opt of configurableProduct.configurableOptions) {
      if (opt.values.length === 1) {
        auto[opt.attributeCode] = opt.values[0].id;
      }
    }
    if (Object.keys(auto).length > 0) {
      setSelectedOptions((prev) => ({ ...auto, ...prev }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.id]);

  // Track product view on mount (only once per product)
  useEffect(() => {
    if (!hasTrackedView.current || trackedProductId.current !== product.id) {
      trackViewItem(product);
      hasTrackedView.current = true;
      trackedProductId.current = product.id;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.id]); // Only depend on product.id, not the whole object or trackViewItem

  const handleOptionSelect = (attributeCode: string, valueId: string) => {
    setSelectedOptions((prev) => ({
      ...prev,
      [attributeCode]: valueId,
    }));
  };

  const handleGroupedQuantityChange = (sku: string, qty: number) => {
    setGroupedQuantities((prev) => ({
      ...prev,
      [sku]: Math.max(0, qty),
    }));
  };

  const handleBundleChange = (
    optionId: number,
    selections: Array<{ uid: string; quantity: number }>,
  ) => {
    setBundleSelections((prev) => {
      const next = { ...prev };
      if (selections.length === 0) delete next[optionId];
      else next[optionId] = selections;
      return next;
    });
  };

  // Find selected variant based on selected options
  const getSelectedVariant = () => {
    if (!configurableProduct) return null;

    const selectedOptionEntries = Object.entries(selectedOptions);
    // Only match if ALL options are selected
    if (
      selectedOptionEntries.length !==
      configurableProduct.configurableOptions.length
    )
      return null;

    // Find variant that matches all selected options
    return (
      configurableProduct.variants.find((variant) => {
        return selectedOptionEntries.every(
          ([attributeCode, selectedValueId]) => {
            // Find the configurable option to get the value details
            const option = configurableProduct.configurableOptions.find(
              (opt) => opt.attributeCode === attributeCode,
            );
            const optionValue = option?.values.find(
              (v) => v.id === selectedValueId,
            );

            if (!optionValue) return false;

            // Find the variant's attribute for this code
            const variantAttr = variant.attributes.find(
              (attr) => attr.code === attributeCode,
            );
            if (!variantAttr) return false;

            // Try multiple matching strategies:
            // 1. Direct value match (for dummy data adapter)
            if (variantAttr.value === optionValue.value) return true;

            // 2. Try decoding base64 uid to extract value_index (for Magento)
            // Magento UIDs are base64 encoded like "configurable/{attr_id}/{value_index}"
            try {
              const decoded = atob(optionValue.id);
              const parts = decoded.split("/");
              const valueIndex = parts[parts.length - 1];
              if (variantAttr.value === valueIndex) return true;
            } catch {
              // Not a valid base64 string, skip
            }

            // 3. Direct id match
            if (variantAttr.value === selectedValueId) return true;

            return false;
          },
        );
      }) ?? null
    );
  };

  const selectedVariant = getSelectedVariant();

  // For configurable products without a selected variant, show the base/regular price only
  // The minimum_price from Magento includes the cheapest variant's discounted price,
  // but we only want to show discounts when a specific variant is selected
  const getDisplayPrice = (): PriceType => {
    if (isBundle && bundleProduct) {
      const configured = validateBundleSelections(bundleProduct.bundleOptions, bundleSelections);
      const total = computeBundleTotal(bundleProduct.bundleOptions, bundleSelections);
      const final = configured ? total : product.price.final;
      return { regular: final, final, currency: product.price.currency, discount: undefined };
    }

    if (selectedVariant) {
      const variantPrice = selectedVariant.price;
      const baseRegularPrice = product.price.regular;

      // Check if variant has a discount compared to the base product's regular price
      if (variantPrice.final < baseRegularPrice) {
        const discountAmount = baseRegularPrice - variantPrice.final;
        const discountPercent = Math.round(
          (discountAmount / baseRegularPrice) * 100,
        );

        return {
          regular: baseRegularPrice, // Show base price as reference
          final: variantPrice.final,
          currency: variantPrice.currency,
          discount: {
            amount: discountAmount,
            percent: discountPercent,
          },
        };
      }

      // No discount compared to base price - show variant price as-is
      return {
        regular: variantPrice.final,
        final: variantPrice.final,
        currency: variantPrice.currency,
        discount: undefined,
      };
    }

    if (isConfigurable) {
      // For configurable products with no variant selected,
      // show regular price without discount (it's the "starting at" price)
      return {
        regular: product.price.regular,
        final: product.price.regular, // Use regular as final when no variant selected
        currency: product.price.currency,
        discount: undefined, // No discount shown until variant is selected
      };
    }

    // For simple/grouped products, check for actual discount
    const { regular, final, currency } = product.price;

    if (final < regular) {
      const discountAmount = regular - final;
      const discountPercent = Math.round((discountAmount / regular) * 100);

      return {
        regular,
        final,
        currency,
        discount: {
          amount: discountAmount,
          percent: discountPercent,
        },
      };
    }

    // No discount
    return {
      regular: final,
      final,
      currency,
      discount: undefined,
    };
  };

  const displayPrice = getDisplayPrice();
  // Status code 3 in the ERP's status text ("Tillfälligt slut", "Produkten är
  // såld!") outranks Magento's stock status, which calls these in stock because
  // Jeeves allows backorders on them. See lib/product-extras.ts.
  const soldOutLabel = extras?.soldOutLabel;
  const magentoStock = selectedVariant?.stock ?? product.stock;
  const displayStock = soldOutLabel ? { ...magentoStock, inStock: false } : magentoStock;
  const displaySku = selectedVariant?.sku ?? product.sku;

  // The option groups still waiting for a choice.
  //
  // Held apart from `canAddToCart` because it is the one blocker the customer
  // can clear themselves. p.7 of the feedback asks for "gör val först" on a
  // press, and a `disabled` button cannot deliver it: it takes neither a click
  // nor focus, so there is no press to respond to and nothing for a screen
  // reader to find. The button stays live and this drives the message instead.
  const missingOptions = configurableProduct
    ? configurableProduct.configurableOptions.filter(
        (option) => !selectedOptions[option.attributeCode],
      )
    : [];

  const canAddToCart = () => {
    if (!displayStock.inStock) return false;

    // Grouped products need at least one item with quantity > 0
    if (isGrouped) {
      return Object.values(groupedQuantities).some((qty) => qty > 0);
    }

    if (isBundle && bundleProduct) {
      return validateBundleSelections(bundleProduct.bundleOptions, bundleSelections);
    }

    if (!isConfigurable) return true;

    // Check all configurable options are selected
    const requiredOptions =
      configurableProduct?.configurableOptions.length ?? 0;
    return Object.keys(selectedOptions).length >= requiredOptions;
  };

  // A grouped product adds its children rather than itself, so its own SKU never
  // reaches the cart for the gift mutation to validate against — no offer there.
  const giftOffer = isGrouped ? null : freeGift;
  const giftAvailable = giftOffer?.inStock === true;

  // The ERP will not sell this online ("Ej säljbar" in Jeeves). Orthogonal to
  // stock — 603 of the 616 products carrying it are in stock — so it blocks the
  // sale on its own. See lib/product-extras.ts.
  const notSellableOnline = extras?.notSellableOnline === true;

  // Dead only where pressing could achieve nothing. An unanswered option group
  // is deliberately NOT in here — see `missingOptions`.
  const addToCartBlocked =
    notSellableOnline ||
    !displayStock.inStock ||
    (!canAddToCart() && missingOptions.length === 0);

  const handleAddToCart = async () => {
    if (addToCartBlocked || isAddingToCart.current) return;

    // Answerable, so say what is missing rather than failing silently.
    if (missingOptions.length > 0) {
      setOptionPromptCount((count) => count + 1);
      return;
    }

    isAddingToCart.current = true;
    // The gift can only be claimed once the qualifying product is in the cart —
    // the backend validates that. `addToCart` runs inside a transition and
    // resolves before the server call settles, so the claim hangs off the
    // engine's success edge rather than an await below.
    const wantsGift = claimGift && giftAvailable;
    // No success toast on add — the drawer sliding open is the confirmation.
    notify({
      error: tToast("addToCartError"),
      onSuccess: () => {
        openCartDrawer();
        if (wantsGift) claimFreeGift(product.sku, quantity);
      },
    });

    try {
      if (isGrouped) {
        // Build grouped items array with quantities > 0
        const groupedItems = Object.entries(groupedQuantities)
          .filter(([_, qty]) => qty > 0)
          .map(([sku, qty]) => ({ sku, quantity: qty }));

        await addToCart({
          sku: product.sku,
          quantity: 1, // Not used for grouped products
          groupedItems,
        });
      } else if (isBundle) {
        await addToCart({
          sku: product.sku,
          quantity,
          selectedOptions: buildSelectedOptionUids(bundleSelections),
        });
      } else {
        await addToCart({
          sku: product.sku,
          quantity,
          selectedOptions: Object.values(selectedOptions),
        });
      }

      // Track add to cart event (only after successful add)
      trackAddToCart(product, quantity);
    } finally {
      isAddingToCart.current = false;
    }
  };

  // Everything the redesigned buy box derives from the Torsbo attribute set.
  // All of it comes off `product.attributes`, which the adapter already builds
  // from the product query's `custom_attributesV2` — no extra round trip.
  const specs = selectSpecs(product.attributes);
  const technical = technicalSpecs(product.attributes);
  const licensed = requiresLicense(product.attributes);
  // Orthogonal to `licensed`: a suppressor is NOT licenspliktig, it requires
  // proof of a licence the customer already holds. See lib/license-proof.ts.
  const needsLicenseProof = requiresLicenseProof(
    (product.categories ?? []).map((category) => category.id),
  );
  const pickupOnly = isPickupOnly(product.attributes);
  const brand = extras?.brand ?? attributeValue(product.attributes, "th_fabrikat");
  const kind = productKind(product.attributes, product.categories?.[0]?.name);
  const eyebrow = [brand, kind].filter(Boolean).join(" · ");
  // Which part of the shop this is, which decides the licence wording, the
  // delivery paragraph and the trust badges. The adapter builds each category's
  // `urlKey` as its full ancestor path, so the section falls out of the first
  // path segment — see lib/product-sections.ts.
  const section = resolveSection(
    (product.categories ?? []).map((category) => category.urlKey),
  );

  const bundleIncomplete =
    isBundle &&
    bundleProduct != null &&
    !validateBundleSelections(bundleProduct.bundleOptions, bundleSelections);

  const hasRealDiscount =
    displayPrice.discount != null &&
    displayPrice.regular > displayPrice.final &&
    displayPrice.discount.amount > 0;

  // Torsbo's own reference price: for a paket, already validated server-side as
  // higher than the package price; for anything else, shown under the price codes
  // the old site showed it for. Stood down when Magento reports a real discount:
  // that compare-at price is the one the product is actually being sold against,
  // and two struck-through figures side by side say nothing.
  const referencePrice = hasRealDiscount
    ? null
    : paket
      ? paket.referencePrice
      : referencePriceFor(extras, displayPrice.final);

  // The listing card shows one badge because the canvas gives each card one.
  // A product page has room to state every marker that applies, and each is a
  // different fact about the item rather than a ranking of the same one.
  const badges: { key: string; tone: BadgeTone }[] = [];
  if (hasRealDiscount) badges.push({ key: "badgeCampaign", tone: "campaign" });
  if (extras?.isUsed) badges.push({ key: "badgeUsed", tone: "condition" });
  if (extras?.isNew) badges.push({ key: "badgeNew", tone: "accent" });
  if (extras?.isLeftHanded) badges.push({ key: "badgeLeftHanded", tone: "accent" });

  const sections: AccordionSection[] = [];
  if (descriptionContent) {
    sections.push({
      id: "description",
      title: t("productDescription"),
      // Clamped with a "Läs mer" (p.2): this section is open by default, and
      // the longest descriptions in the catalogue push the specifications,
      // the delivery terms and the recommended products off the screen.
      content: <ReadMore>{descriptionContent}</ReadMore>,
    });
  }
  if (technical.length > 0) {
    sections.push({
      id: "specifications",
      title: t("specifications"),
      content: <SpecTable specs={technical} />,
    });
  }
  sections.push({
    id: "delivery",
    // "Leverans" on its own where no licence is involved (p.8); the licence
    // half of the heading is earned by the product, not the category.
    title: t(deliveryTitleKey(licensed)),
    content: (
      <DeliveryInfo
        section={section}
        pickupOnly={pickupOnly}
        requiresLicense={licensed}
        notSellableOnline={notSellableOnline}
      />
    ),
  });

  return (
    <>
      {/* Explicit row/column placement rather than nesting the gallery and the
          accordion in one column element: it keeps the buy box beside the
          gallery on desktop while leaving the DOM order gallery → buy box →
          accordion, so on mobile the add-to-cart button is not pushed below
          three collapsed sections. */}
      <div className="grid gap-8 lg:grid-cols-[1.2fr_minmax(0,1fr)] lg:gap-14">
        <div className="min-w-0 lg:col-start-1 lg:row-start-1">
          {/* Not `?? product.images`: Magento returns an empty ARRAY for a
              variant with no photos of its own, which `??` does not treat as
              absent — see lib/variant-images.ts. */}
          {/* A paket shows its composite photo only. Images 2 and 3 of a paket
              are close-ups of the individual components, which the includes
              grid below already shows as cards — and cutting the set to one
              also removes the thumbnail strip, since ImageGallery only draws it
              for more than one image. */}
          <ImageGallery
            images={
              paket
                ? galleryImages(selectedVariant, product.images).slice(0, 1)
                : galleryImages(selectedVariant, product.images)
            }
            productName={product.name}
          />
        </div>

        {/* Buy box */}
        <div className="flex min-w-0 flex-col lg:col-start-2 lg:row-start-1 lg:row-span-2">
          {/* Drawn whenever there is a stock claim to make, in or out of
              stock: gating it on `inStock` left a sold-out product with no
              other badge showing no stock wording at all. */}
          {(badges.length > 0 || !notSellableOnline || paket) && (
            <div className="mb-3.5 flex flex-wrap gap-2">
              {/* Leads the row, as the canvas draws it: what kind of price this
                  is comes before whether it is in stock. */}
              {paket && <ProductBadge tone="accent">{t("paketPriceBadge")}</ProductBadge>}

              {badges.map((badge) => (
                <ProductBadge key={badge.key} tone={badge.tone}>
                  {t(badge.key)}
                </ProductBadge>
              ))}
              {/* No stock claim at all when the ERP says the product cannot be
                  bought online: "Slut i lager" is the wording p.5 asks us to
                  stop showing, and "I lager i Torsbo" beside a dead buy button
                  reads as a fault. The reason is stated by the button instead. */}
              {!notSellableOnline && (
                <span
                  className={
                    displayStock.inStock
                      ? "inline-block rounded-[2px] bg-success/15 px-2.5 py-1 text-[10.5px] font-bold uppercase leading-none tracking-[0.08em] text-success-600"
                      : "inline-block rounded-[2px] bg-foreground/10 px-2.5 py-1 text-[10.5px] font-bold uppercase leading-none tracking-[0.08em] text-foreground/70"
                  }
                >
                  {displayStock.inStock
                    ? t("inStockInStore")
                    : (soldOutLabel ?? t("outOfStock"))}
                </span>
              )}
              {/* Not beside "Kan ej köpas online": the badge says how an
                  online order is handed over, and there is no order to hand
                  over. 353 of the 354 products carrying that status carry this
                  flag too, so without the guard the pairing is the norm. */}
              {pickupOnly && !notSellableOnline && (
                <ProductBadge tone="condition">{t("pickupOnly")}</ProductBadge>
              )}
            </div>
          )}

          {eyebrow && (
            <span className="font-mono text-xs uppercase tracking-[0.12em] text-foreground/50">
              {eyebrow}
            </span>
          )}

          <h1 className="mb-4 mt-2.5 font-heading text-[28px] font-semibold leading-[1.1] text-heading sm:text-[34px] lg:text-[40px]">
            {product.name}
          </h1>

          <div className="flex flex-wrap items-baseline gap-x-3.5 gap-y-1">
            <span className="font-heading text-[30px] font-bold leading-none text-accent-700 lg:text-[36px]">
              {bundleIncomplete
                ? `${formatPrice(product.price.final, product.price.currency)} – ${formatPrice(bundleProduct!.maxPrice, product.price.currency)}`
                : formatPrice(displayPrice.final, displayPrice.currency)}
            </span>
            {hasRealDiscount && !bundleIncomplete && (
              <span className="text-[15px] text-foreground/40 line-through">
                {formatPrice(displayPrice.regular, displayPrice.currency)}
              </span>
            )}
            {referencePrice != null && (
              <span
                data-testid={paket ? "paket-reference-price" : "reference-price"}
                className="text-[15px] text-foreground/40 line-through"
              >
                {formatPrice(referencePrice, displayPrice.currency)}
              </span>
            )}
            <span className="text-sm text-foreground/45">{t("inclVat")}</span>
            {hasRealDiscount && displayPrice.discount!.percent > 0 && (
              <span className="bg-success/10 px-2.5 py-1 text-xs font-semibold text-success-600">
                {t("save", { percent: Math.round(displayPrice.discount!.percent) })}
              </span>
            )}
          </div>

          {/* Its own line, as the canvas draws it. Says only what the figures
              support: NOT the canvas's "mot köp var för sig", which the
              catalogue contradicts — on 9010108 the parts are 505 kr cheaper
              bought separately than the package they make up. */}
          {/* A paket only: the old site printed no savings line for an ordinary
              product quoted against its reference price, only the figure. */}
          {paket && referencePrice != null && (
            <span className="mt-1 text-[13px] font-semibold text-success-600">
              {t("paketSavings", {
                amount: formatPrice(referencePrice - displayPrice.final, displayPrice.currency),
              })}
            </span>
          )}

          <span className="mb-6 mt-1.5 text-[13px] text-foreground/50">
            {t("articleNumber", { sku: displaySku })}
          </span>

          {/* Everything below the price is a stack of independent modules, most
              of them conditional. One `space-y-5` on the container gives them a
              single 20px rhythm and, because an absent module renders no node at
              all, the spacing stays right whichever ones a product has. They
              used to carry their own margins, which compounded — the spec grid's
              24px plus the volume table's 16px read as a 40px hole. */}
          <div className="space-y-5">
            {/* Not for a paket: these cells are the rifle's calibre, barrel
                length and thread — facts about one component, printed where
                they read as facts about the package. The full table is still in
                the accordion for anyone who wants them. */}
            {!paket && <SpecGrid specs={specs} />}

            {/* Volume / tier pricing */}
            {product.price.tiers && product.price.tiers.length > 0 && (
              <VolumePricingTable tiers={product.price.tiers} quantity={quantity} />
            )}

            {/* Configurable Options */}
            {configurableProduct && (
              <div className="space-y-6">
                {configurableProduct.configurableOptions.map((option) => {
                  // Sorted for display because Magento's order is not stable
                  // between requests — see lib/configurable-options.ts.
                  const values = sortOptionValues(option.values);
                  const selected = selectedOptions[option.attributeCode];

                  return (
                    <div key={option.id}>
                      <h2
                        className="mb-3 text-sm font-medium uppercase tracking-wide text-muted"
                        id={`option-${option.id}`}
                      >
                        {option.label}
                      </h2>

                      {/* A long list becomes a dropdown: 33 wrapped buttons
                          bury the buy button, which is what p.12 asks about. */}
                      {values.length >= OPTIONS_AS_SELECT_FROM ? (
                        <select
                          aria-labelledby={`option-${option.id}`}
                          value={selected ?? ""}
                          onChange={(e) =>
                            handleOptionSelect(option.attributeCode, e.target.value)
                          }
                          className="w-full rounded-sm border border-border bg-input px-3 py-2.5 text-sm text-foreground focus:border-accent-600 focus:outline-none"
                        >
                          <option value="">{t("selectPlaceholder")}</option>
                          {values.map((value) => (
                            <option key={value.id} value={value.id}>
                              {value.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        // Large swatches go four to a row and stretch to fill
                        // the column: a reticle is a fine line drawing, and
                        // Torsbo asked for as much width as the buy box has.
                        // Everything else wraps at its natural size.
                        <div
                          className={cn(
                            swatchSize(option.attributeCode) === 'lg'
                              ? 'grid grid-cols-4 gap-3'
                              : 'flex flex-wrap gap-3',
                          )}
                        >
                          {values.map((value) => (
                            <OptionButton
                              key={value.id}
                              value={value}
                              isSelected={selected === value.id}
                              size={swatchSize(option.attributeCode)}
                              onClick={() =>
                                handleOptionSelect(option.attributeCode, value.id)
                              }
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Grouped Product Items */}
            {groupedProduct && (
              <div className="space-y-4">
                <h2 className="mb-3 text-sm font-medium uppercase tracking-wide text-muted">
                  {t("selectItems")}
                </h2>
                <GroupedProductItems
                  items={groupedProduct.items}
                  quantities={groupedQuantities}
                  onChange={handleGroupedQuantityChange}
                />
              </div>
            )}

            {/* Bundle Options */}
            {bundleProduct && (
              <BundleOptions
                options={bundleProduct.bundleOptions}
                state={bundleSelections}
                onChange={handleBundleChange}
                currency={product.price.currency}
              />
            )}

            {/* Free gift opt-in */}
            <FreeGiftOffer
              gift={giftOffer}
              checked={claimGift}
              onChange={setClaimGift}
              currency={product.price.currency}
              disabled={isLoading}
            />

            {/* Quantity, buy button and favourite share one row: the buy button
                takes the slack between the two fixed-width controls. Below `sm`
                it drops to its own full-width row, where three controls side by
                side would squeeze the label. */}
            <div className="flex flex-wrap items-center gap-3">
              {!isGrouped && (
                <div className="flex items-center gap-2">
                  <QuantitySelector
                    value={quantity}
                    onChange={setQuantity}
                    min={rules.min}
                    step={rules.step}
                    max={rules.max}
                  />
                  {/* "à 50 st": the box counts units, so without this the
                      jump from 1000 to 1050 would read as a fault. */}
                  {rules.step > 1 && (
                    <span className="whitespace-nowrap text-[13px] text-foreground/60">
                      {t("packSize", { count: rules.step })}
                    </span>
                  )}
                </div>
              )}

              <Button
                size="lg"
                className="order-last w-full rounded sm:order-none sm:w-auto sm:flex-1"
                onClick={handleAddToCart}
                disabled={addToCartBlocked}
                isLoading={isLoading}
              >
                {t("addToCart")}
              </Button>

              {/* The design system's Button defaults to `rounded-lg` — the
                  previous design's 8px. `cn` runs through tailwind-merge, so
                  overriding the radius here brings both controls onto the
                  canvas's 4px without restyling a primitive the cart and
                  account pages also use. */}
              <WishlistButton productSku={product.sku} size="lg" className="rounded" />
            </div>

            {/* "Gör val först" (p.7). Named groups, not a bare sentence: this
                product has two, and "make a selection" does not say which one
                is still open. `role="alert"` so it is announced on the press
                that caused it — the message appears below the button, which a
                screen reader would otherwise never travel back to. */}
            {/* Why the button is dead. Static, not an alert: it is true of the
                product rather than a response to a press, so it has to be
                readable before anyone tries. */}
            {notSellableOnline && (
              <p className="mt-3 text-sm font-medium text-foreground/70">
                {t("notSellableOnline")}
              </p>
            )}

            {/* Red and moving, because the olive version went unnoticed beside
                a button that now looks pressable. `motion-safe:` is Tailwind's
                prefers-reduced-motion: no-preference — the same way round
                globals.css guards smooth scrolling, so a reader who asks for
                less motion keeps the colour and the words.

                Keyed on the press count: choosing an option re-renders this in
                place (no key change, no movement), while another press remounts
                it and replays the shake. */}
            {optionPromptCount > 0 && missingOptions.length > 0 && (
              <p
                key={optionPromptCount}
                role="alert"
                className="mt-3 text-sm font-semibold text-error-600 motion-safe:animate-shake"
              >
                {t("selectOptionsFirst", {
                  options: missingOptions.map((option) => option.label).join(", "),
                })}
              </p>
            )}

            {/* Its own line rather than a fourth control in the row above: the
                row is already three wide at `sm`, and this is a browsing aid
                rather than part of the buy decision. */}
            <CompareCheckbox sku={product.sku} className="mt-3" />

            {licensed && <LicenseNotice section={section} />}

            {/* Above the fold with the licence notice, not in the accordion:
                it is a condition of the purchase, so it has to be read before
                the product goes in the cart rather than discovered at checkout —
                which is the delay the shop asked us to remove. */}
            {needsLicenseProof && <LicenseProofNotice />}

            {/* The contents as a receipt beside the price. Renders nothing
                without a paket, so the stack's rhythm is unaffected elsewhere. */}
            {paket && <PaketIncludesList items={paket.items} price={displayPrice} />}
          </div>
        </div>

        {/* Short description and the stacked sections, under the gallery */}
        <div className="min-w-0 lg:col-start-1 lg:row-start-2">
          {shortDescriptionContent}

          {/* Above the accordion, where the canvas puts it: the contents are
              part of understanding what the package IS, so they come before the
              description, specification and delivery sections. */}
          {paket && <PaketIncludes items={paket.items} />}

          <ProductAccordion sections={sections} className="mt-5" />
        </div>
      </div>

      {/* Sticky Add to Cart for Mobile */}
      <StickyAddToCart
        product={product}
        onAddToCart={handleAddToCart}
        isLoading={isLoading}
        disabled={addToCartBlocked}
      />
    </>
  );
}
